import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setFormStatus('success');
      setFormMessage('Thanks for subscribing!');
      setEmail('');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d99lqlj558j8vijmh4n0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('Thanks for subscribing!');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || 'Something went wrong.';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-primary-900 text-background-50">
      <div className="w-full px-4 md:px-6">
        <div className="py-14 md:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="flex items-center gap-2 text-background-50 hover:text-accent-300 transition-colors duration-200 mb-4">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-file-list-3-line text-xl text-accent-300"></i>
              </div>
              <span className="font-heading text-lg font-bold whitespace-nowrap">Hipobuy Sheet</span>
            </Link>
            <p className="text-sm text-background-400 leading-relaxed">
              The most comprehensive Hipobuy spreadsheet resource hub. Discover organized product finds, QC guides, and expert shopping strategies.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-background-300 uppercase tracking-wider mb-4">Categories</h4>
            <div className="space-y-2.5">
              {categories.slice(0, 6).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block text-sm text-background-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-background-300 uppercase tracking-wider mb-4">Quick Links</h4>
            <div className="space-y-2.5">
              <Link to="/" className="block text-sm text-background-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap">
                Home
              </Link>
              <Link to="/hipobuy-spreadsheet-guide" className="block text-sm text-background-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap">
                Spreadsheet Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-sm text-background-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap"
              >
                Hipobuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block text-sm text-background-300 hover:text-background-50 transition-colors duration-150 whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-background-300 uppercase tracking-wider mb-4">Subscribe</h4>
            <p className="text-sm text-background-400 leading-relaxed mb-4">
              Get the latest Hipobuy spreadsheet updates, new finds, and shopping tips delivered to your inbox.
            </p>
            <form
              data-readdy-form
              onSubmit={handleSubscribe}
              className="flex flex-col gap-2"
            >
              <div className="newsletter-honeypot">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 bg-transparent border-b border-background-600 px-0 py-2 text-sm text-background-50 placeholder:text-background-500 focus:outline-none focus:border-accent-300 transition-colors duration-200"
                />
                <button
                  type="submit"
                  className="ml-2 px-4 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Join
                </button>
              </div>
              {formStatus === 'success' && (
                <p className="text-xs text-accent-300 mt-1">{formMessage}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-xs text-accent-400 mt-1">{formMessage}</p>
              )}
            </form>
          </div>
        </div>

        <div className="py-8 border-t border-background-700/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-background-500">
              &copy; {new Date().getFullYear()} Hipobuy Spreadsheet Hub. All rights reserved.
            </p>
            <p className="text-xs text-background-600">
              This site is an independent resource and is not affiliated with Hipobuy.
            </p>
          </div>
        </div>

        <div className="pb-8">
          <div className="font-heading text-6xl md:text-8xl font-bold text-background-800/30 tracking-tighter select-none whitespace-nowrap overflow-hidden">
            HIPOBUY
          </div>
        </div>
      </div>
    </footer>
  );
}